import React, { useState } from 'react';

interface LocationCategory {
  id: string;
  name: string;
  icon: string;
  items: string[];
}

export function extractGoogleMapsSrc(input?: string): string {
  if (!input) return '';
  const trimmed = input.trim();
  const srcMatch = trimmed.match(/src=["']([^"']+)["']/i);
  if (srcMatch && srcMatch[1]) {
    return srcMatch[1];
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return '';
}

interface BrandLocationProps {
  data?: {
    to_airport_title?: string;
    description?: string;
    car_time?: string;
    bicycle_time?: string;
    jogging_time?: string;
    map_image?: string;
    map_iframe?: string;
    map_url?: string;
    categories?: LocationCategory[];
  };
}

const DEFAULT_CATEGORIES: LocationCategory[] = [
  {
    id: 'entertainment',
    name: 'Entertainment',
    icon: 'ri-goblet-line',
    items: [
      'Bliss Family Entertainment',
      'Aviation Social Center',
      'Accra Mall',
      '805 Restaurant',
      'Polo Club Restaurant',
      'Marina Mall'
    ]
  },
  {
    id: 'business',
    name: 'Business',
    icon: 'ri-building-line',
    items: [
      'Stanbic Bank',
      'Absa',
      'CalBank',
      'Access',
      'Standard Chartered',
      'CBG',
      'Fidelity Bank'
    ]
  },
  {
    id: 'education',
    name: 'Education',
    icon: 'ri-book-read-line',
    items: [
      'Safari Association Intl School',
      'East Airport International School',
      'École Française Adkel',
      'Alsyd School'
    ]
  },
  {
    id: 'more',
    name: 'More',
    icon: 'ri-more-line',
    items: [
      'Nyaho Medical Center',
      'Compu Ghana',
      'Can West',
      'CFAO',
      'Stanbic Heights',
      'Silver Star Towers'
    ]
  }
];

export const BrandLocation: React.FC<BrandLocationProps> = ({ data }) => {
  const description =
    data?.description ||
    "Golden Tower is located in Accra’s most prestigious and celebrated neighbourhood, Airport Residential Area. Commuting from Golden Tower to the Accra International Airport via car takes 5 minutes, via bicycle, 8 minutes, and by jogging, 20 minutes. In addition, we are surrounded by industry-class hubs such as";

  const toAirportTitle = data?.to_airport_title || 'To Airport';
  const carTime = data?.car_time || '5 minutes';
  const bikeTime = data?.bicycle_time || '8 minutes';
  const jogTime = data?.jogging_time || '20 minutes';
  const mapImage =
    data?.map_image ||
    'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&w=1200&q=80';

  const rawIframe = data?.map_iframe || data?.map_url;
  const parsedIframe = extractGoogleMapsSrc(rawIframe);
  // Default to Google Maps embed for Airport Residential Area, Accra if nothing set
  const iframeSrc = parsedIframe || 'https://maps.google.com/maps?q=Airport%20Residential%20Area,%20Accra,%20Ghana&t=&z=15&ie=UTF8&iwloc=&output=embed';

  const categories =
    data?.categories && data.categories.length > 0 ? data.categories : DEFAULT_CATEGORIES;

  const [activeCatId, setActiveCatId] = useState(categories[0]?.id || 'entertainment');
  const activeCat = categories.find((c) => c.id === activeCatId) || categories[0];

  return (
    <section id="location" className="relative py-28 md:py-36 bg-[#0a0a0f] text-white border-t border-white/5 overflow-hidden">
      {/* Background glow */}
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#C5A46D]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C5A46D]/10 border border-[#C5A46D]/30 text-[#C5A46D] text-xs uppercase tracking-[0.25em] font-medium">
            Accra Prime Address
          </div>
          <h2 className="text-3xl md:text-5xl font-light tracking-wide text-white">
            Unrivalled <span className="font-semibold text-[#C5A46D]">Location</span>
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#C5A46D] to-transparent mx-auto" />
          <p className="text-gray-300 text-base md:text-lg leading-relaxed font-light">
            {description}
          </p>
        </div>

        {/* Commute Destination Title (To Airport) */}
        <div className="text-center mt-8 sm:mt-10 mb-6 sm:mb-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-[#E5A93C] tracking-wide">
            {toAirportTitle}
          </h3>
        </div>

        {/* Commute Time Badges */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-16">
          <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#C5A46D]/40 transition-colors">
            <div className="w-12 h-12 rounded-xl bg-[#C5A46D]/10 border border-[#C5A46D]/20 flex items-center justify-center shrink-0 text-[#C5A46D]">
              <i className="ri-car-line text-2xl"></i>
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider">Via Car</div>
              <div className="text-lg font-medium text-white">{carTime}</div>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#C5A46D]/40 transition-colors">
            <div className="w-12 h-12 rounded-xl bg-[#C5A46D]/10 border border-[#C5A46D]/20 flex items-center justify-center shrink-0 text-[#C5A46D]">
              <i className="ri-riding-line text-2xl"></i>
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider">Via Bicycle</div>
              <div className="text-lg font-medium text-white">{bikeTime}</div>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#C5A46D]/40 transition-colors">
            <div className="w-12 h-12 rounded-xl bg-[#C5A46D]/10 border border-[#C5A46D]/20 flex items-center justify-center shrink-0 text-[#C5A46D]">
              <i className="ri-run-line text-2xl"></i>
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider">By Jogging</div>
              <div className="text-lg font-medium text-white">{jogTime}</div>
            </div>
          </div>
        </div>

        {/* Categories Tab Navigation */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-10">
          {categories.map((cat) => {
            const isActive = cat.id === activeCatId;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCatId(cat.id)}
                className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-xs md:text-sm uppercase tracking-[0.16em] font-medium transition-all duration-300 cursor-pointer border ${
                  isActive
                    ? 'bg-[#C5A46D] text-black border-[#C5A46D] shadow-lg shadow-[#C5A46D]/20'
                    : 'bg-white/5 text-gray-300 border-white/10 hover:border-white/30 hover:text-white'
                }`}
              >
                <i className={`${cat.icon || 'ri-map-pin-line'} text-base`}></i>
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Tab Content Display */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-[#111118] border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl">
          
          {/* Items Grid */}
          <div className="lg:col-span-6 space-y-6">
            <div>
              <span className="text-xs font-semibold text-[#C5A46D] tracking-[0.2em] uppercase">
                Surrounding Hubs
              </span>
              <h3 className="text-2xl md:text-3xl font-light text-white mt-1">
                {activeCat?.name} Destinations
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
              {activeCat?.items?.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3.5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#C5A46D]/30 hover:bg-white/[0.05] transition-all group"
                >
                  <div className="w-2 h-2 rounded-full bg-[#C5A46D] group-hover:scale-125 transition-transform" />
                  <span className="text-sm text-gray-200 font-light group-hover:text-white transition-colors">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Map / Google Maps iframe */}
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[16/10] min-h-[350px] border border-white/10 shadow-2xl bg-[#0c0c12]">
              {iframeSrc ? (
                <iframe
                  src={iframeSrc}
                  className="w-full h-full min-h-[350px] border-0 rounded-2xl"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Golden Tower Location Map"
                />
              ) : (
                <>
                  <img
                    src={mapImage}
                    alt="Golden Tower Accra Location"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                </>
              )}
              
              <div className="absolute bottom-4 left-4 pointer-events-none flex items-center gap-3 bg-black/85 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/15 shadow-xl">
                <div>
                  <div className="text-sm font-semibold text-white">Airport Residential Area</div>
                  <div className="text-[11px] text-[#C5A46D] tracking-wider uppercase font-medium">Accra, Ghana</div>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#C5A46D] text-black flex items-center justify-center font-bold shadow-md">
                  <i className="ri-map-pin-2-fill text-base"></i>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default BrandLocation;
